import {
  Alert,
  Button,
  Card,
  CopyButton,
  Group,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { semanticColor } from "@how/design-system";
import { ConfirmActionModal, ErrorState, LoadingState, PageHeader } from "@how/ui";
import { instantToZoned, zonedToInstant } from "./block-schedule";
import {
  useRotateWebsiteBypass,
  useSaveWebsiteOffline,
  useWebsiteOffline,
  type RotatedBypass,
  type WebsiteOffline,
} from "./offline-queries";
import { WebsiteTabs } from "./WebsiteTabs";

/** The cover's default sentence, as the public site shows it when the church wrote none. */
export const DEFAULT_OFFLINE_MESSAGE =
  "We're making some changes to our website. Please check back soon.";

/** The same rules as set_website_offline, so a refusal is seen while typing. */
export const offlineSchema = z.object({
  enabled: z.boolean(),
  message: z
    .string()
    .trim()
    .max(300, "Keep the message to 300 characters")
    .regex(/^[^\n\r\t]*$/, "One line only"),
  backDate: z.string(),
  backTime: z.string(),
});
type OfflineForm = z.infer<typeof offlineSchema>;

/** The return time the form holds as the instant it names, null for none, or an error sentence. */
export function backAtFrom(
  values: Pick<OfflineForm, "backDate" | "backTime">,
  timeZone: string,
  now: Date = new Date(),
): { readonly backAt: string | null } | { readonly error: string } {
  if (values.backDate === "") return { backAt: null };
  const instant = zonedToInstant(values.backDate, values.backTime, timeZone);
  if (instant === null) return { error: "Choose a date, and a time if you want one." };
  if (instant.getTime() <= now.getTime())
    return { error: "The time your website is back must be in the future." };
  return { backAt: instant.toISOString() };
}

function formFrom(state: WebsiteOffline): OfflineForm {
  const zoned = state.backAt === null ? null : instantToZoned(state.backAt, state.timeZone);
  return {
    enabled: state.enabled,
    message: state.message,
    backDate: zoned?.date ?? "",
    backTime: zoned?.time ?? "",
  };
}

/**
 * Website → Settings (ADR 0079): whether the website is online, the cover page's words and return time, and the
 * bypass link that lets a chosen audience see the real site while it is offline. Someone who manages the website
 * (website.manage) changes it; the database refuses everyone else.
 */
export function SettingsPage() {
  const state = useWebsiteOffline();
  return (
    <>
      <PageHeader
        title="Website"
        description="Your public website: its pages, menus, images, look, forms and settings."
      />
      <WebsiteTabs active="settings" />
      {state.isPending ? (
        <LoadingState rows={4} />
      ) : state.isError ? (
        <ErrorState
          message="Could not load your website's settings."
          onRetry={() => {
            void state.refetch();
          }}
        />
      ) : (
        <Stack gap="md">
          <OfflineCard state={state.data} />
          <BypassCard state={state.data} />
        </Stack>
      )}
    </>
  );
}

function OfflineCard({ state }: { readonly state: WebsiteOffline }) {
  const save = useSaveWebsiteOffline();
  const heading = useRef<HTMLHeadingElement>(null);
  const form = useForm<OfflineForm>({
    mode: "controlled",
    initialValues: formFrom(state),
    validate: zod4Resolver(offlineSchema),
  });
  // A save answers with the state as stored; the form starts again from it, so Save reads as done.
  useEffect(() => {
    form.setInitialValues(formFrom(state));
    form.resetDirty();
  }, [state.enabled, state.message, state.backAt, state.timeZone]);

  const submit = form.onSubmit((values) => {
    const back = backAtFrom(values, state.timeZone);
    if ("error" in back) {
      form.setFieldError("backDate", back.error);
      return;
    }
    save.mutate(
      { enabled: values.enabled, message: values.message.trim(), backAt: back.backAt },
      {
        onSuccess: (saved) => {
          // Save turns disabled once saved; focus goes to the card's heading, never to the page.
          heading.current?.focus();
          notifications.show({
            color: "teal",
            title: saved.enabled ? "Your website is offline" : "Your website is online",
            message: saved.enabled
              ? "Visitors see your cover page within about 30 seconds. Your staff dashboard and member portal stay up."
              : "Visitors see your website again within about 30 seconds.",
          });
        },
        onError: (error) => {
          notifications.show({ color: "red", title: "Not saved", message: error.message });
        },
      },
    );
  });

  return (
    <Card component="section" aria-labelledby="website-offline-title" data-website-offline>
      <form onSubmit={submit} noValidate>
        <Stack gap="md">
          <Title order={2} size="h4" id="website-offline-title" ref={heading} tabIndex={-1}>
            {state.enabled ? "Your website is offline" : "Your website is online"}
          </Title>
          <Text size="sm" c="dimmed">
            Take your website offline for a demo, a big change, or a problem. Visitors see one cover
            page with your logo, your message and when you expect to be back, and nothing else. Your
            staff dashboard and member portal stay up. Put it back here when you are ready.
          </Text>
          {!state.website ? (
            <Alert color={semanticColor("warning")} variant="light" title="No website">
              Your plan has no HoW website, so there is nothing to take offline.
            </Alert>
          ) : null}
          <Switch
            label="Take my website offline"
            {...form.getInputProps("enabled", { type: "checkbox" })}
          />
          <Text size="sm" c="dimmed">
            While it is offline, every page shows the cover page and search engines are told to come
            back later.
          </Text>
          <Textarea
            label="Message on the cover page"
            description={`Up to 300 characters. Leave it empty for: "${DEFAULT_OFFLINE_MESSAGE}"`}
            placeholder={DEFAULT_OFFLINE_MESSAGE}
            maxLength={300}
            autosize
            minRows={2}
            {...form.getInputProps("message")}
          />
          <Stack gap={4}>
            <Text component="h3" fw={600} size="sm" m={0}>
              When you expect to be back (optional)
            </Text>
            <Text size="sm" c="dimmed">
              Shown on the cover page, on your church&apos;s clock ({state.timeZone}). Leave the
              date empty if you are not sure.
            </Text>
            <Group gap="sm" grow align="flex-start">
              <TextInput type="date" label="Back on" {...form.getInputProps("backDate")} />
              <TextInput type="time" label="At" {...form.getInputProps("backTime")} />
            </Group>
          </Stack>
          <Group justify="flex-end">
            <Button type="submit" loading={save.isPending} disabled={!form.isDirty()}>
              Save
            </Button>
          </Group>
        </Stack>
      </form>
    </Card>
  );
}

/**
 * The bypass link: anyone who opens it sees the real website while it is offline, from that browser, until a new link
 * is made. HoW keeps only a fingerprint of the link, so it is shown once, right after it is made.
 */
function BypassCard({ state }: { readonly state: WebsiteOffline }) {
  const rotate = useRotateWebsiteBypass();
  const [confirming, setConfirming] = useState(false);
  const [fresh, setFresh] = useState<RotatedBypass | null>(null);
  const linkField = useRef<HTMLInputElement>(null);
  const makeButton = useRef<HTMLButtonElement>(null);
  const link = fresh?.bypassUrl ?? null;

  return (
    <Card component="section" aria-labelledby="website-bypass-title" data-website-bypass>
      <Stack gap="md">
        <Title order={2} size="h4" id="website-bypass-title">
          Let some people see the website while it is offline
        </Title>
        <Text size="sm" c="dimmed">
          Share this link with anyone who should see the real website while it is offline: a demo
          audience, your board, a designer. Opening it once lets that browser in; everyone else
          keeps seeing the cover page. Making a new link stops the old one and everyone who used it.
        </Text>
        {state.siteUrl === null ? (
          <Alert color={semanticColor("info")} variant="light" title="No website address yet">
            Your website has no live address, so there is no link to share yet.
          </Alert>
        ) : null}
        {link !== null ? (
          <Stack gap="xs">
            <TextInput
              label="Your bypass link"
              description="HoW shows it only now. Copy it somewhere safe; if you lose it, make a new one."
              value={link}
              readOnly
              ref={linkField}
              onFocus={(event) => {
                event.currentTarget.select();
              }}
            />
            <Group gap="xs">
              <CopyButton value={link}>
                {({ copied, copy }) => (
                  <Button variant="default" onClick={copy} aria-live="polite">
                    {copied ? "Copied" : "Copy link"}
                  </Button>
                )}
              </CopyButton>
              <Button component="a" href={link} target="_blank" rel="noreferrer" variant="default">
                Open your website (opens in a new tab)
              </Button>
            </Group>
          </Stack>
        ) : (
          <Text size="sm">
            {state.bypassRotatedAt === null
              ? "You have not made a link yet."
              : `A link was last made on ${new Date(state.bypassRotatedAt).toLocaleDateString()}. HoW cannot show it again; make a new one if you need it.`}
          </Text>
        )}
        <Group>
          <Button
            ref={makeButton}
            variant={link === null ? "filled" : "default"}
            disabled={state.siteUrl === null}
            onClick={() => {
              setConfirming(true);
            }}
          >
            {link === null ? "Make a new link" : "Make another link"}
          </Button>
        </Group>
      </Stack>
      <ConfirmActionModal
        opened={confirming}
        title="Make a new bypass link?"
        message={
          state.bypassRotatedAt === null
            ? "Anyone with the new link will see the real website while it is offline."
            : "The link you made before stops working, and so does everyone who used it. Anyone with the new link will see the real website while it is offline."
        }
        confirmLabel="Make a new link"
        loading={rotate.isPending}
        onClose={() => {
          setConfirming(false);
          makeButton.current?.focus();
        }}
        onConfirm={() => {
          rotate.mutate(undefined, {
            onSuccess: (rotated) => {
              setFresh(rotated);
              setConfirming(false);
              notifications.show({
                color: "teal",
                title: "New link made",
                message: "Copy it now; HoW shows it only once.",
              });
              // The field is the thing to act on next; the button that opened the dialog stays where it was.
              window.setTimeout(() => linkField.current?.focus(), 0);
            },
            onError: (error) => {
              setConfirming(false);
              notifications.show({ color: "red", title: "No new link", message: error.message });
              makeButton.current?.focus();
            },
          });
        }}
      />
    </Card>
  );
}
